import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import {
    eInvoiceQueryKey,
    useEInvoiceErrorText,
} from '../../hooks/use-e-invoice'
import { markEInvoiceWrong } from '../../services'

interface MarkEInvoiceWrongDialogProps {
    open: boolean
    onClose: () => void
    invoiceID: number
}

/**
 * Xác nhận hoá đơn điện tử bị sai (POST .../e-invoice/mark-wrong). Hệ thống chỉ ghi nhận;
 * huỷ / thay thế hoá đơn kế toán làm trên phần mềm kế toán. Lý do bắt buộc.
 */
const MarkEInvoiceWrongDialog = ({
    open,
    onClose,
    invoiceID,
}: MarkEInvoiceWrongDialogProps) => {
    const { t } = useTranslation('invoice')
    const errorText = useEInvoiceErrorText()
    const queryClient = useQueryClient()
    const [reason, setReason] = useState('')

    const { mutateAsync: markWrong, isPending } = useMutationWithHandler(
        { mutationFn: markEInvoiceWrong },
        {
            reactQueryKeys: QUERY_KEYS.INVOICE_E_INVOICE,
            showSuccessToast: false,
            onSuccess: (response) => {
                if (response?.result)
                    queryClient.setQueryData(
                        eInvoiceQueryKey(invoiceID),
                        response.result
                    )
                toast.success(t('e-invoice-mark-wrong-success'))
                onClose()
            },
            // useMutationWithHandler không tra namespace invoice: tự toast bằng errorText
            showErrorToast: false,
            onError: (error) => toast.error(errorText(error)),
        }
    )

    const trimmedReason = reason.trim()

    return (
        <Dialog
            open={open}
            onClose={() => !isPending && onClose()}
            maxWidth="md"
            title={t('e-invoice-mark-wrong-title')}
            actions={
                <div className="flex w-full justify-end gap-2">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="error"
                        onClick={() =>
                            markWrong({ invoiceID, reason: trimmedReason })
                        }
                        loading={isPending}
                        disabled={!trimmedReason || isPending}
                    >
                        {t('e-invoice-mark-wrong-confirm')}
                    </Button>
                </div>
            }
        >
            <div className="space-y-3 text-sm">
                <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    {t('e-invoice-mark-wrong-warning')}
                </div>
                <label className="block">
                    <span className="mb-1 block font-medium text-gray-700">
                        {t('e-invoice-mark-wrong-reason')}{' '}
                        <span className="text-red-500">*</span>
                    </span>
                    <textarea
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder={t(
                            'e-invoice-mark-wrong-reason-placeholder'
                        )}
                        rows={3}
                        disabled={isPending}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none"
                    />
                </label>
            </div>
        </Dialog>
    )
}

export default MarkEInvoiceWrongDialog
